import type { JunkDisqualificationReason, LeadStage } from '@/types/lead';

/**
 * Pick the stage a disqualified lead should be filed under.
 *
 * `isLost` alone cannot identify it: the seeded set gives both "Lost" and
 * "Disqualified" `isLost: true`, so a lookup on that flag files every
 * disqualification under Lost. `isDisqualified` is the field that separates
 * them — see `isDisqualifiedStage` below, which prefers it and keeps the old
 * name match only for a stage served without the column.
 *
 * That fallback is the point. Before any of this, a tenant who renamed the
 * stage got `undefined`, the caller omitted `stageId` entirely, and the lead was
 * marked DISQUALIFIED while still sitting in whatever open column it came from.
 * Degrading to "closed as lost" is wrong in the label but right in the
 * lifecycle; staying open is wrong in both.
 *
 * Deactivated stages are excluded: filing a lead into one hides it from the
 * board completely, which is worse than the bug this guards against.
 *
 * Sorted by `sortOrder` then `id`, then preferring the flagged stage — the whole
 * rule, in that order, matching `LeadsService.resolveBlockedLeadStageId`. Both
 * halves have to match for the two to agree, and for a while only the sort did:
 * the backend picked purely by order, so on the seeded board a rep disqualifying
 * a lead by hand filed it under "Disqualified" while the automatic paths
 * (blocked contact, duplicate merge) filed theirs under "Lost".
 *
 * The `id` tie-break is not cosmetic: `sortOrder` has no uniqueness constraint,
 * and `Array.prototype.sort` is stable, so without it a tie is resolved by
 * whatever order the API happened to return. Compared with `<`/`>` rather than
 * `localeCompare`, because these are canonical lowercase UUIDs and code-unit
 * order matches the byte order Postgres sorts a `uuid` column by;
 * `localeCompare` applies locale rules that need not agree.
 *
 * Both copies exist because a service boundary sits between them, so a change
 * here needs the same change there. They no longer disagree over a *name*,
 * which is what the flag bought; they still have to agree on the ordering.
 *
 * Returns `undefined` only when the tenant has no active lost stage at all, and
 * the caller must then omit `stageId` rather than invent one.
 */
export function resolveDisqualifiedStage(stages: LeadStage[] | undefined): LeadStage | undefined {
  const lostStages = terminalStages(stages);
  return lostStages.find(isDisqualifiedStage) ?? lostStages[0];
}

/**
 * The other terminal, picked by *exclusion* rather than by the name "Lost".
 *
 * Mirrors `LeadsService.pickLostStage`, and for the same reason it exists there:
 * "Lost" is only the seeded label, and it sorting first is exactly the
 * assumption a reordered board breaks. Excluding the disqualified stage holds
 * whatever the remaining column is called.
 *
 * Falls back to the first lost stage when every candidate is the disqualified
 * one — a tenant running a single terminal column shares it between both
 * outcomes. The outcome still splits; only the board cannot show the split.
 */
function resolveLostStage(stages: LeadStage[] | undefined): LeadStage | undefined {
  const lostStages = terminalStages(stages);
  return lostStages.find((stage) => !isDisqualifiedStage(stage)) ?? lostStages[0];
}

/**
 * U-TERM1. Pick the column that matches the terminal the reason implies.
 *
 * The server derives the outcome from the reason — SPAM and DUPLICATE are
 * DISQUALIFIED, everything else is a plain LOST — and files the lead in the
 * column that matches. Sending `resolveDisqualifiedStage` for every reason (as
 * this modal did) asked it to put a lead lost on price in the Disqualified
 * column while its row read LOST — the board and the outcome describing the
 * same close differently.
 *
 * `LeadsService.update` now overrides a contradicting `stageId` rather than
 * storing the disagreement, so this is no longer the only thing standing
 * between the two. It is still worth sending the right column: the request then
 * describes what actually happens, and the caller is not told one placement and
 * given another.
 *
 * `reason` stays a bare `string` rather than the junk union: it is asked on
 * every render, including before one is chosen, and other callers hold the six
 * loss reasons this dialog no longer offers. An empty or unrecognised value is
 * a loss — only the junk list is junk, matching the server, where mistaking a
 * real loss for junk drops it out of the loss-reason breakdown entirely.
 */
export function resolveTerminalStage(
  stages: LeadStage[] | undefined,
  reason: string | undefined,
): LeadStage | undefined {
  return reason !== undefined && isJunkDisqualificationReason(reason)
    ? resolveDisqualifiedStage(stages)
    : resolveLostStage(stages);
}

/**
 * The junk terminal's reasons, mirroring the backend's
 * `isJunkDisqualification`. OTHER is excluded on purpose there and here: an
 * uncategorised loss is still a loss.
 *
 * Exported because `DisqualifyLeadModal` offers exactly these and nothing else —
 * building its options from this list rather than repeating them means the
 * classifier and the picker cannot come to disagree about what counts as junk.
 */
export const JUNK_DISQUALIFICATION_REASONS = [
  'SPAM',
  'DUPLICATE',
] as const satisfies readonly JunkDisqualificationReason[];

const JUNK_REASON_LOOKUP: ReadonlySet<string> = new Set(JUNK_DISQUALIFICATION_REASONS);

/**
 * Narrows a value from a `<Select>` — which hands back a bare `string` — onto
 * the junk union, so a dialog offering only junk reasons cannot end up holding
 * something else in state.
 */
export function isJunkDisqualificationReason(value: string): value is JunkDisqualificationReason {
  return JUNK_REASON_LOOKUP.has(value);
}

/**
 * Which of the two terminals a lost stage is.
 *
 * The flag answers whenever the column reached us. It used to be the name
 * alone, on both sides of the wire, which made the routing depend on a string
 * tenants may edit: renaming this column, or translating the board, sent every
 * disqualification to Lost while the row still recorded
 * `outcome = 'DISQUALIFIED'`.
 *
 * Checked with `typeof` rather than the shorter `isDisqualified || name-match`,
 * matching `LeadsService.isDisqualifiedStage`: the name is the fallback for a
 * stage served *without* the field — an operations build predating the column —
 * not for one that carries it as `false`. Truthiness cannot tell those two
 * apart, and re-deriving an honest `false` from the name would overrule a
 * tenant who deliberately cleared the flag on a column still called
 * "Disqualified", which is the string they were trying to stop being read.
 */
export const isDisqualifiedStage = (stage: LeadStage): boolean =>
  typeof stage.isDisqualified === 'boolean'
    ? stage.isDisqualified
    : stage.name.toLowerCase().includes('disqualified');

/** Every stage that can hold a closed lead, in the order both sides agree on. */
function terminalStages(stages: LeadStage[] | undefined): LeadStage[] {
  return (stages ?? [])
    .filter((stage) => stage.isLost && stage.isActive)
    .sort((a, b) => {
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      if (a.id === b.id) return 0;
      return a.id < b.id ? -1 : 1;
    });
}
